/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

import { createCombobox } from '../lib/combobox.js';
import { getConfig } from '../lib/config.js';
import {
    injectParams,
    isExternalLink,
    stripPathForApp,
} from '../lib/links.js';

    export function init() {
    const cfg = getConfig() || {};

    const box = document.querySelector('.search-box');
    if (!box) return;

    const btn = document.querySelector('.search-button');
    const btnIn = document.querySelector('.search-button-inner');
    const close = document.querySelector('.search-box .map-button');
    const form = box.querySelector('.search-form');
    const field = form.querySelector('.search-field');
    const reset = form.querySelector('.search-reset');
    const result = box.querySelector('.js-search-result');
    const filters = JSON.parse(form.getAttribute('data-filters') || '{}');
    const noMsg = form.getAttribute('data-no-results-msg') || '';
    const allMsg = form.getAttribute('data-all-results-msg') || '';
    const dropped = 'dropped';
    const access =
        new URLSearchParams(location.search).get('access') ||
        getConfig()?.access?.token;
    const searchURL = injectParams(cfg.endpoints.find, {
        lang: cfg.i18n.lang,
        preview: cfg.map.isPreview ? 1 : null
    });
    const suggestURL = injectParams(
        cfg.endpoints.wordSuggestions,
        {
            lang: cfg.i18n.lang,
            preview: cfg.map.isPreview ? 1 : null
        }
    );

    const open = () => {
        btn.classList.add(dropped);
        box.classList.add(dropped);
        btn?.setAttribute('aria-expanded', 'true');
    };
    const closeAll = () => {
        btn.classList.remove(dropped);
        box.classList.remove(dropped);
        btn?.setAttribute('aria-expanded', 'false');
    };

    if (btn) btn.addEventListener('click', open);
    if (btnIn) btnIn.addEventListener('click', closeAll);
    if (close) close.addEventListener('click', closeAll);
    document.addEventListener('click', (e) => {
        if (!box.classList.contains(dropped)) return;
        if (!box.contains(e.target) && !btn.contains(e.target)) closeAll();
    });

    createCombobox(field, {
        minLength: 3,
        delay: 500,
        appendTo: form,
        source: async (term) => {
            const response = await fetch(injectParams(suggestURL, { term }), {
                cache: 'no-store'
            });
            if (!response.ok) throw response;
            return response.json();
        },
        // Taking a suggestion runs the search, which is what closing the
        // old widget used to do.
        onSelect: () => runSearch()
    });

    const renderFilters = (meta) => {
        const total = meta.total || 0;
        const counts = meta.type_counts || {};
        const items = Object.keys(filters)
            .map(
                (k) =>
                    `<li class="search-filter" data-filter-type="${k}">${filters[k]} (${counts[k] ?? 0})</li>`
            )
            .join('');
        return `<div class="search-filters list-wrapper"><p class="search-result-message js-list-toggle">${allMsg} (${total})</p><ul>${items}</ul></div>`;
    };
    const renderMessage = (meta) =>
        meta.total
            ? renderFilters(meta)
            : `<p class="search-result-message search-result-message--no-results">${noMsg}</p>`;

    /*
     * Run the search and render its results.
     *
     * Callers invoke this rather than dispatching a `submit` event at the
     * form. A synthetic one has to be built `cancelable` for the
     * handler's `preventDefault` to mean anything, and one built without
     * it let the form navigate — reloading the page instead of searching
     * in place. There is nothing to cancel if no event is fired.
     */
    const runSearch = async () => {
        field?.blur();
        const params = new URLSearchParams(new FormData(form));
        try {
            const r = await fetch(`${searchURL}&${params.toString()}`, {
                method: 'GET',
                credentials: 'same-origin'
            });
            if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
            const res = await r.json();
            let out = `${renderMessage(res.meta)}<ul${res.className ? ` class="${res.className}"` : ''}>`;
            (res.results || []).forEach((r) => {
                if (r.url) {
                    const mapUrl = new URL(location.href);
                    const itemUrl = new URL(r.url, location.href);
                    if (mapUrl.host === itemUrl.host)
                        itemUrl.search = mapUrl.search;
                    r.url = stripPathForApp(itemUrl.toString());
                }
                if (!isExternalLink(r.url) && access)
                    r.url = injectParams(r.url, { access });
                out += `<li class="search-result" data-type="${r.type}">${r.url ? `<a href="${r.url}">` : ''}<p><span>${r.display_type}</span><strong>${r.title}</strong></p>${r.subhead ? `<p>${r.subhead}</p>` : ''}<p><i>${r.snippet}</i></p>${r.url ? '</a>' : ''}</li>`;
            });
            out += '</ul>';
            result.innerHTML = out;
            // filter toggles (native)
            [...result.querySelectorAll('.search-filter')].forEach((el) => {
                el.addEventListener('click', (ev) => {
                    ev.preventDefault();
                    el.classList.toggle('active');
                    const active = [
                        ...result.querySelectorAll('.search-filter.active')
                    ].map((n) => n.dataset.filterType);
                    [...result.querySelectorAll('.search-result')].forEach(
                        (li) => {
                            li.style.display = active.length
                                ? active.includes(li.dataset.type)
                                    ? ''
                                    : 'none'
                                : '';
                        }
                    );
                });
            });
            // close panel on link click
            [...result.querySelectorAll('a')].forEach((a) => {
                a.addEventListener('click', () => {
                    closeAll();
                    a.classList.add('visited');
                });
            });
        } catch (e) {
            console.error('Error loading search results:', e);
        }
    };

    form.addEventListener('submit', (e) => {
        e.preventDefault();
        runSearch();
    });

    reset?.addEventListener('click', (e) => {
        e.preventDefault();
        // Clearing is not a search for nothing: the empty query returns
        // no results, and rendering "nothing found" for a field the
        // visitor has just emptied reads as a failure. Put the panel back
        // the way it opened and leave them ready to type.
        field.value = '';
        result.innerHTML = '';
        field.focus();
    });
}
